/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, TextField, InputAdornment, MenuItem, Autocomplete } from "@mui/material";
import { WidgetProps } from "@rjsf/utils";
import { inputStyles, renderLabel, renderDescriptionTooltip } from "./common";

// Helper to resolve $ref if still present
const resolveRef = (ref: string | undefined, rootSchema: any): any => {
  if (!ref || !rootSchema?.definitions) return null;
  const refPath = ref.replace("#/definitions/", "");
  return rootSchema.definitions[refPath];
};

// Helper to extract enum options from a schema
const extractEnumOptions = (schema: any): Array<{ value: any; label: string }> => {
  if (!schema) return [];
  const options: Array<{ value: any; label: string }> = [];

  // oneOf with const or enum values
  if (schema.oneOf && Array.isArray(schema.oneOf)) {
    schema.oneOf.forEach((option: any) => {
      if (option.const !== undefined) {
        options.push({
          value: option.const,
          label: option.title || (option.const === null ? "None" : String(option.const)),
        });
      } else if (option.enum && Array.isArray(option.enum)) {
        // Nested enum in oneOf
        const enumNames = option.enumNames;
        option.enum.forEach((val: any, index: number) => {
          options.push({
            value: val,
            label: enumNames?.[index] || (val === null ? "None" : String(val)),
          });
        });
      }
    });
  } else if (schema.enum && Array.isArray(schema.enum)) {
  // Direct enum
    const enumNames = schema.enumNames;
    schema.enum.forEach((val: any, index: number) => {
      options.push({
        value: val,
        label: enumNames?.[index] || (val === null ? "None" : String(val)),
      });
    });
  }

  // anyOf with const or enum values
  if (schema.anyOf && Array.isArray(schema.anyOf)) {
    schema.anyOf.forEach((option: any) => {
      if (option.const !== undefined) {
        options.push({
          value: option.const,
          label: option.title || (option.const === null ? "None" : String(option.const)),
        });
      } else if (option.enum && Array.isArray(option.enum)) {
        // Nested enum in anyOf
        const enumNames = option.enumNames;
        option.enum.forEach((val: any, index: number) => {
          options.push({
            value: val,
            label: enumNames?.[index] || (val === null ? "None" : String(val)),
          });
        });
      }
    });
  }

  return options;
};

// Main logic to find options recursively
const findOptions = (s: any, rootSchema: any): Array<{ value: any; label: string }> => {
  const opts = extractEnumOptions(s);
  if (opts.length > 0) return opts;

  // Resolve $ref
  if (s.$ref) {
    const resolved = resolveRef(s.$ref, rootSchema);
    if (resolved) return findOptions(resolved, rootSchema);
  }

  // Check array items (for multi-select fields)
  if (s.type === "array" && s.items) {
    return findOptions(s.items, rootSchema);
  }

  // Recurse through anyOf
  if (s.anyOf && Array.isArray(s.anyOf)) {
    for (const option of s.anyOf) {
      const subOpts = findOptions(option, rootSchema);
      if (subOpts.length > 0) opts.push(...subOpts);
    }
  }

  // Recurse through oneOf
  if (s.oneOf && Array.isArray(s.oneOf)) {
    for (const option of s.oneOf) {
      const subOpts = findOptions(option, rootSchema);
      if (subOpts.length > 0) opts.push(...subOpts);
    }
  }

  return opts;
};

// Select Widget - uses MUI Select for better appearance
// Automatically switches to Autocomplete for large lists (>10 items)
export const SelectWidget = (props: WidgetProps) => {
  const { id, value, label, onChange, required, disabled, readonly, schema, registry, options } = props;
  
  const rootSchema = registry?.rootSchema;

 let enumOptions: Array<{ value: any; label: string }> = [];

  // First, check if UI schema provides enumOptions directly
  if (options?.enumOptions && Array.isArray(options.enumOptions) && !schema.oneOf && !schema.anyOf) {
    enumOptions = options.enumOptions;
  } else {
    // Try to extract options from the schema
    enumOptions = findOptions(schema, rootSchema);
  }

  // Try to find description
  let displayDescription = schema.description;
  if (!displayDescription && schema.$ref) {
    const resolved = resolveRef(schema.$ref, rootSchema);
    displayDescription = resolved?.description;
  }

  // Helper to get the actual property name from the RJSF ID
  const getPropertyNameFromId = (id: string): string | undefined => {
    // RJSF IDs look like root_propertyName or root_arrayName_0_propertyName
    // They can also have suffixes like __anyof_select
    const parts = id.split(/[__|_]/).filter(
      (p) => p !== "" && p !== "root" && p !== "anyof" && p !== "select" && isNaN(Number(p))
    );
    return parts.pop();
  };

  // If no options found, try to find definition by field name
  if (enumOptions.length === 0 && rootSchema) {
    const propertyName = getPropertyNameFromId(id);

    if (propertyName) {
      // 1. Try definitions first
      if (rootSchema.definitions?.[propertyName]) {
        const definition = rootSchema.definitions[propertyName];
        enumOptions = findOptions(definition, rootSchema);
        if (!displayDescription && typeof definition === "object" && definition !== null) {
          displayDescription = (definition as any).description;
        }
      }

      // 2. Try root properties if still no options
      if (enumOptions.length === 0 && rootSchema.properties?.[propertyName]) {
        const propertySchema = rootSchema.properties[propertyName];
        enumOptions = findOptions(propertySchema, rootSchema);
        if (!displayDescription && typeof propertySchema === "object" && propertySchema !== null) {
          displayDescription = (propertySchema as any).description;
        }
      }
    }
  }

  // Debug warning if no options found
  if (enumOptions.length === 0) {
    console.warn("SelectWidget: No enum options found", {
      fieldLabel: label,
      id,
      schemaKeys: Object.keys(schema),
      schemaType: schema.enum ? "enum" : schema.oneOf ? "oneOf" : schema.anyOf ? "anyOf" : schema.type,
      hasItems: !!schema.items,
      hasRef: !!schema.$ref,
    });
  }

  // Treat null as unselected; drop null options entirely so placeholder is used instead
  const filteredEnumOptions = enumOptions.filter((opt) => opt.value !== null);
  const hasOptions = filteredEnumOptions.length > 0;

  const isNullish = value === null || value === undefined;

  // Use Autocomplete for large lists
  if (filteredEnumOptions.length > 10) {
    // Handle type coercion: if value doesn't match exactly, try coercing to option type
    let selectedOption: typeof filteredEnumOptions[number] | undefined = filteredEnumOptions.find((opt) => opt.value === value);
    if (!selectedOption && value !== undefined && value !== null && filteredEnumOptions.length > 0) {
      // Try matching by coercing value to same type as first option
      const optionType = typeof filteredEnumOptions[0].value;
      const coercedValue = optionType === "string" ? String(value) : optionType === "number" ? Number(value) : value;
      selectedOption = filteredEnumOptions.find((opt) => opt.value === coercedValue);
    }
    // If still no match, set to undefined
    selectedOption = isNullish ? undefined : selectedOption || undefined;

    return (
      <Autocomplete
        id={id}
        fullWidth
        options={filteredEnumOptions}
        getOptionLabel={(option) => option.label || String(option.value)}
        isOptionEqualToValue={(option, val) => option.value === val?.value}
        value={selectedOption || null}
        onChange={(_event, newValue) => {
          onChange(newValue ? newValue.value : undefined);
        }}
        disabled={disabled || readonly}
        renderInput={(params) => (
          <TextField
            {...params}
            label={renderLabel(label, !!required)}
            required={required}
            fullWidth
            size="small"
            variant="outlined"
            sx={inputStyles}
            placeholder={!selectedOption ? "Unknown" : undefined}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                  {displayDescription && (
                    <InputAdornment position="end" sx={{ mr: 1 }}>
                      {renderDescriptionTooltip(displayDescription)}
                    </InputAdornment>
                  )}
                </>
              ),
            }}
          />
        )}
      />
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        "& .MuiOutlinedInput-root": {
          position: "relative",
        },
        "& .description-icon-overlay": {
          position: "absolute",
          right: "36px",
          top: "50%",
          transform: "translateY(-50%)",
          display: "flex",
          alignItems: "center",
          pointerEvents: "none",
          zIndex: 1,
          "& > *": { pointerEvents: "auto" },
        },
      }}
    >
      <TextField
        id={id}
        select
        value={
          (() => {
            // If we have enum options, check if current value matches one
            if (hasOptions) {
              // Exact match
              const exactMatch = filteredEnumOptions.find((opt) => opt.value === value);
              if (exactMatch) return value;

              // Try type coercion for incomplete matches
              if (value !== undefined && value !== null) {
                const optionType = typeof filteredEnumOptions[0].value;
                const coercedValue = optionType === "string" ? String(value) : optionType === "number" ? Number(value) : value;
                const coercedMatch = filteredEnumOptions.find((opt) => opt.value === coercedValue);
                if (coercedMatch) return coercedValue;
              }
            }
            // Empty string means show placeholder "Unknown"
            return "";
          })()
        }
        onChange={(event) => {
          const selectedValue = event.target.value;
          // Pass the selected value directly, or undefined if empty
          onChange(selectedValue === "" ? undefined : selectedValue);
        }}
        disabled={disabled}
        slotProps={{
          input: {
            endAdornment: displayDescription ? (
              <Box className="description-icon-overlay">
                {renderDescriptionTooltip(displayDescription)}
              </Box>
            ) : null,
          },
          select: {
            readOnly: readonly,
          },
        }}
        label={renderLabel(label, !!required)}
        required={required}
        fullWidth
        size="small"
        variant="outlined"
        sx={{
          ...inputStyles,
          "& .MuiSelect-select": {
            textAlign: "left",
            paddingRight: displayDescription ? "64px !important" : undefined,
          },
        }}
      >
        <MenuItem value="">
          <em>Unknown</em>
        </MenuItem>
        {filteredEnumOptions.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
};
